import React, { useState, useEffect, useRef } from 'react';
import '../../styles/teacher/MainContent.css';
import jsPDF from 'jspdf';
import { BarChart, Bar, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { fetchStudentStats } from '../../utils/studentStatsService';
import { fetchTeacherProfile } from '../../utils/teacherService';
import { supabase } from '../../utils/supabase';
import { getStudentSubjectScores } from '../../utils/studentSubjectScoresService';
import { fetchEnrolledStudents } from '../../utils/enrolledStudentsService';
import { getApiUrl } from '../../utils/apiConfig';

const MainContent = ({ activePage, selectedStudent }) => {
  const [showPerformanceGraph, setShowPerformanceGraph] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [studentSubjectData, setStudentSubjectData] = useState([]);
  const [loadingSubjectData, setLoadingSubjectData] = useState(false);
  const [studentStats, setStudentStats] = useState({
    totalStudentsEnrolled: 0,
    studentsCompletedSession: 0
  });
  const [loading, setLoading] = useState(true);
  const [teacherProfile, setTeacherProfile] = useState({
    fullName: 'Teacher',
    schoolName: 'Loading...',
    role: 'Loading...',
    subject: 'Loading...'
  });
  const [teacherLoading, setTeacherLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [iepFileName, setIepFileName] = useState('');
  const iepInputRef = useRef(null);

  // Reset the selected IEP/504 file when a different student is opened
  useEffect(() => {
    setIepFileName('');
  }, [selectedStudent]);

  const handleIepFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setIepFileName(file.name);
    }
    e.target.value = '';
  };

  // Fetch student statistics on component mount
  useEffect(() => {
    const loadStudentStats = async () => {
      try {
        const stats = await fetchStudentStats();
        if (stats) {
          setStudentStats(stats);
        }
      } catch (error) {
        console.error('Failed to load student stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStudentStats();
  }, []);

  // Fetch teacher profile data on component mount
  useEffect(() => {
    const loadTeacherProfile = async () => {
      try {
        console.log('🔄 Loading teacher profile for main content...');
        
        // Get user ID from Supabase session
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          console.log('👤 Fetching profile for user:', user.id);
          const profile = await fetchTeacherProfile(user.id);
          
          if (profile) {
            setTeacherProfile(profile);
            console.log('✅ Teacher profile loaded for main content:', profile);
          } else {
            console.warn('⚠️ Teacher profile not found, using defaults');
          }
        } else {
          console.warn('⚠️ No user session found');
        }
      } catch (error) {
        console.error('❌ Error loading teacher profile:', error);
      } finally {
        setTeacherLoading(false);
      }
    };

    loadTeacherProfile();
  }, []);

  // Fetch recent activity from student quiz completions and emotion feedback
  useEffect(() => {
    const loadRecentActivity = async () => {
      try {
        const studentsData = await fetchEnrolledStudents();
        
        if (studentsData && studentsData.students && studentsData.students.length > 0) {
          const allActivities = [];
          
          // For each student, fetch their quiz data and emotion feedback
          for (const student of studentsData.students) {
            try {
              const response = await fetch(getApiUrl(`student-details?user_id=${student.user_id}`));
              if (response.ok) {
                const studentDetails = await response.json();
                
                // Add quiz completions
                if (studentDetails.quizzes && studentDetails.quizzes.length > 0) {
                  const latestQuiz = studentDetails.quizzes[0];
                  allActivities.push({
                    id: `${student.user_id}_quiz_${latestQuiz.quiz_id}`,
                    studentName: student.name,
                    type: 'quiz',
                    quizTitle: latestQuiz.title || 'Quiz completed',
                    subjectName: latestQuiz.subject_name || 'General',
                    isCorrect: latestQuiz.is_correct,
                    timestamp: latestQuiz.created_at,
                    icon: latestQuiz.is_correct ? '✅' : '📝'
                  });
                }
                
                // Add emotion feedback
                if (studentDetails.emotion_feedback && studentDetails.emotion_feedback.length > 0) {
                  const latestEmotion = studentDetails.emotion_feedback[0];
                  const emotionText = latestEmotion.emotion || latestEmotion.after_reading_emotions || 'unspecified';
                  const emotionEmoji = getEmotionEmoji(emotionText);
                  allActivities.push({
                    id: `${student.user_id}_emotion_${latestEmotion.timestamp}`,
                    studentName: student.name,
                    type: 'emotion',
                    emotion: emotionText,
                    timestamp: latestEmotion.timestamp,
                    icon: emotionEmoji
                  });
                }
              }
            } catch (error) {
              console.error(`Error fetching details for student ${student.name}:`, error);
            }
          }
          
          // Sort all activities by timestamp and get top 5
          const sortedActivities = allActivities
            .filter(activity => activity !== null && activity.timestamp)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, 5);
          
          setRecentActivity(sortedActivities);
        }
      } catch (error) {
        console.error('Error loading recent activity:', error);
        setRecentActivity([]);
      }
    };
    
    loadRecentActivity();
    // Refresh every 30 seconds
    const interval = setInterval(loadRecentActivity, 30000);
    return () => clearInterval(interval);
  }, []);

  // Sample parent data for each student
  const studentParents = {
    'Alice Johnson': { name: 'John Johnson', email: 'john.johnson@email.com' },
    'Bob Smith': { name: 'Mary Smith', email: 'mary.smith@email.com' },
    'Carol Davis': { name: 'David Davis', email: 'david.davis@email.com' },
    'David Wilson': { name: 'Lisa Wilson', email: 'lisa.wilson@email.com' },
    'Emma Brown': { name: 'Robert Brown', email: 'robert.brown@email.com' },
    'Frank Miller': { name: 'Susan Miller', email: 'susan.miller@email.com' }
  };

  // Helper function to calculate time ago
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'recently';
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now - past) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds} sec ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hour ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  // Helper function to get emoji based on emotion
  const getEmotionEmoji = (emotion) => {
    if (!emotion) return '😊';
    const emotionLower = emotion.toLowerCase();
    
    // Map emotions to emojis
    if (emotionLower.includes('happy') || emotionLower.includes('joyful') || emotionLower.includes('excited')) {
      return '😊';
    } else if (emotionLower.includes('sad') || emotionLower.includes('upset') || emotionLower.includes('down')) {
      return '😢';
    } else if (emotionLower.includes('anxious') || emotionLower.includes('worried') || emotionLower.includes('nervous')) {
      return '😰';
    } else if (emotionLower.includes('angry') || emotionLower.includes('mad') || emotionLower.includes('frustrated')) {
      return '😠';
    } else if (emotionLower.includes('tired') || emotionLower.includes('exhausted') || emotionLower.includes('sleepy')) {
      return '😴';
    } else if (emotionLower.includes('confused') || emotionLower.includes('puzzled') || emotionLower.includes('lost')) {
      return '😕';
    } else if (emotionLower.includes('focused') || emotionLower.includes('concentrated') || emotionLower.includes('determined')) {
      return '😐';
    } else if (emotionLower.includes('calm') || emotionLower.includes('relaxed') || emotionLower.includes('peaceful')) {
      return '😌';
    } else if (emotionLower.includes('proud') || emotionLower.includes('accomplished') || emotionLower.includes('successful')) {
      return '😎';
    } else {
      return '😊'; // Default emoji
    }
  };

  const handleShowPerformanceGraph = async () => {
    setShowPerformanceGraph(true);
    
    // Fetch subject data for the selected student
    if (selectedStudent && selectedStudent.user_id) {
      setLoadingSubjectData(true);
      try {
        console.log('📊 Fetching subject scores for student:', selectedStudent.name);
        const data = await getStudentSubjectScores(selectedStudent.user_id);
        
        if (data && data.subject_ranking && data.subject_ranking.length > 0) {
          // Transform the data for the chart (same as student dashboard)
          const defaultColors = ['#4a148c', '#6a1b9a', '#2e7d32', '#1565c0', '#e65100'];
          const transformedData = data.subject_ranking.map((subject, index) => ({
            subject: subject.subject,
            score: Math.round(subject.average_score * 10), // Convert to 0-100 scale
            color: defaultColors[index % defaultColors.length],
            total_score: subject.total_score,
            total_quizzes: subject.total_quizzes,
            accuracy: Math.round(subject.accuracy)
          }));
          
          setStudentSubjectData(transformedData);
          console.log('✅ Student subject scores loaded:', transformedData);
        } else {
          console.log('📊 No subject data available for this student');
          setStudentSubjectData([]);
        }
      } catch (error) {
        console.error('❌ Error fetching student subject scores:', error);
        setStudentSubjectData([]);
      } finally {
        setLoadingSubjectData(false);
      }
    }
  };

  const handleClosePerformanceGraph = () => {
    setShowPerformanceGraph(false);
  };

  const handleSendMessageToParent = () => {
    if (selectedStudent) {
      setShowMessageModal(true);
    }
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setMessageText('');
  };

  const handleSendMessage = () => {
    if (messageText.trim()) {
      const parent = studentParents[selectedStudent.name];
      console.log('Sending message to parent:', parent.name, 'Message:', messageText);
      alert(`Message sent to ${parent.name} (${parent.email})!`);
      setMessageText('');
      setShowMessageModal(false);
    } else {
      alert('Please enter a message.');
    }
  };

  const generateReport = () => {
    if (!selectedStudent) return;

    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    let yPosition = margin;
    
    // Helper function to check if we need a new page
    const checkPageBreak = (requiredSpace = 20) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };
    
    // Helper function to add text with line breaks
    const addText = (text, x, y, options = {}) => {
      doc.text(text, x, y, options);
      return y + (options.fontSize ? options.fontSize / 4 + 5 : 12);
    };
    
    // Page 1: Header and Student Information
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Student Performance Report', 105, yPosition, { align: 'center' });
    yPosition += 25;
    
    // Student information
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Student Information:', margin, yPosition);
    yPosition += 15;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    yPosition = addText(`Name: ${selectedStudent.name || 'N/A'}`, margin, yPosition);
    yPosition = addText(`Username: ${selectedStudent.username || 'N/A'}`, margin, yPosition);
    yPosition = addText(`Current Grade: ${selectedStudent.grade || 'N/A'}`, margin, yPosition);
    yPosition = addText(`School: ${selectedStudent.schoolName || 'N/A'}`, margin, yPosition);
    yPosition = addText(`Status: ${selectedStudent.present ? 'Present' : 'Absent'}`, margin, yPosition);
    
    yPosition += 10;
    
    // Quiz Statistics
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Quiz Performance Summary:', margin, yPosition);
    yPosition += 15;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    if (selectedStudent.quiz_stats) {
      yPosition = addText(`Total Quizzes Completed: ${selectedStudent.quiz_stats.completed_quizzes || 0}`, margin, yPosition);
      yPosition = addText(`Total Score: ${selectedStudent.quiz_stats.total_score || 0} points`, margin, yPosition);
      yPosition = addText(`Correct Answers: ${selectedStudent.quiz_stats.correct_answers || 0}`, margin, yPosition);
      yPosition = addText(`Accuracy Rate: ${Math.round(selectedStudent.quiz_stats.accuracy_rate || 0)}%`, margin, yPosition);
    } else {
      yPosition = addText('No quiz data available', margin, yPosition);
    }
    
    yPosition += 10;
    
    // Learning Profile
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Learning Profile:', margin, yPosition);
    yPosition += 15;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    yPosition = addText(`Learning Buddy: ${selectedStudent.buddy ? selectedStudent.buddy.charAt(0).toUpperCase() + selectedStudent.buddy.slice(1) : 'Not selected'}`, margin, yPosition);
    yPosition = addText(`Favorite Animal: ${selectedStudent.favoriteAnimal || 'N/A'}`, margin, yPosition);
    yPosition = addText(`Relaxation Method: ${selectedStudent.relaxationMethod || 'N/A'}`, margin, yPosition);
    yPosition = addText(`Favorite Snack: ${selectedStudent.favoriteSnack || 'N/A'}`, margin, yPosition);
    
    // Check if we need a new page for subject data
    checkPageBreak(50);
    
    // Page 2: Subject-wise Performance
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Subject-wise Performance:', margin, yPosition);
    yPosition += 15;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    if (studentSubjectData && studentSubjectData.length > 0) {
      studentSubjectData.forEach((subject, index) => {
        // Check if we need a new page for each subject
        checkPageBreak(40);
        
        doc.setFont('helvetica', 'bold');
        doc.text(`${subject.subject}:`, margin, yPosition);
        yPosition += 12;
        
        doc.setFont('helvetica', 'normal');
        yPosition = addText(`  Score: ${subject.score}/100`, margin + 10, yPosition);
        yPosition = addText(`  Quizzes Taken: ${subject.total_quizzes}`, margin + 10, yPosition);
        yPosition = addText(`  Accuracy: ${subject.accuracy}%`, margin + 10, yPosition);
        yPosition = addText(`  Total Points: ${subject.total_score}`, margin + 10, yPosition);
        
        yPosition += 10; // Extra space between subjects
      });
    } else {
      doc.text('No subject data available', margin, yPosition);
      yPosition += 15;
    }
    
    // Add page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount}`, 105, pageHeight - 10, { align: 'center' });
      
      // Add footer with generation info on last page
      if (i === pageCount) {
        doc.text(`Report generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, margin, pageHeight - 25);
        doc.text(`Teacher: ${teacherProfile.fullName || 'Teacher'}`, margin, pageHeight - 15);
      }
    }
    
    // Save the PDF
    const fileName = `${selectedStudent.name.replace(/\s+/g, '_')}_Performance_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    alert('Report generated successfully with latest data!');
  };

  const renderHomeContent = () => (
    <div className="home-content">
      <div className="welcome-section">
        <h1>Welcome back, {teacherLoading ? 'Teacher' : teacherProfile.fullName}!</h1>
        <p>Here's what's happening in your classroom today.</p>
      </div>
      
      <div className="content-grid">
        <div className="content-card">
          <h3>Today's Overview</h3>
          <div className="overview-stats">
            <div className="overview-item">
              <span className="overview-number">{loading ? '...' : studentStats.totalStudentsEnrolled}</span>
              <span className="overview-label">Students Enrolled in an active ITZ session </span>
            </div>
            <div className="overview-item">
              <span className="overview-number">{loading ? '...' : studentStats.studentsCompletedSession}</span>
              <span className="overview-label">No. of Students Completed session</span>
            </div>
          </div>
        </div>

        <div className="content-card">
          <h3>Recent Activity</h3>
          <div className="activity-list">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity) => {
                const timeAgo = getTimeAgo(activity.timestamp);
                return (
                  <div key={activity.id} className="activity-item">
                    <span className="activity-icon">{activity.icon}</span>
                    <span className="activity-text">
                      {activity.type === 'emotion' 
                        ? `${activity.studentName} feels "${activity.emotion}"`
                        : `${activity.studentName} completed ${activity.quizTitle} (${activity.subjectName})`
                      }
                    </span>
                    <span className="activity-time">{timeAgo}</span>
                  </div>
                );
              })
            ) : (
              <div className="activity-item">
                <span className="activity-icon">📝</span>
                <span className="activity-text">No recent activity</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStudentDashboard = () => (
    <div className="student-dashboard-content">
      <div className="content-header">
        <h1>{selectedStudent ? selectedStudent.name : 'Student Details'}</h1>
        <button className="back-btn app-btn" onClick={() => window.location.reload()}>← Back to Home</button>
      </div>
      
      {selectedStudent && (
        <div className="student-profile-section">
          <div className="student-profile-header">
            <div className="student-profile-photo">
              <img src={selectedStudent.image} alt={selectedStudent.name} />
              <div className={`attendance-badge ${selectedStudent.present ? 'present' : 'absent'}`}>
                {selectedStudent.present ? 'Present' : 'Absent'}
              </div>
            </div>
            <div className="student-profile-info">
              <h2>{selectedStudent.name}</h2>
              <div className="student-grade-row">
                <p className="student-grade">Current Grade: {selectedStudent.grade}</p>
                <button
                  type="button"
                  className="back-btn app-btn"
                  onClick={() => iepInputRef.current && iepInputRef.current.click()}
                >
                  Upload IEP/504
                </button>
                <input
                  ref={iepInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={handleIepFileChange}
                />
              </div>
              {iepFileName && <p className="iep-file-name">Selected: {iepFileName}</p>}
              <p className="student-status">Status: {selectedStudent.present ? 'Present Today' : 'Absent Today'}</p>
            </div>
          </div>
          
          <div className="student-stats-grid">
            <div className="stat-card">
              <h3>Quizzes Completed</h3>
              <div className="stat-value">
                {selectedStudent.quiz_stats ? 
                  `${selectedStudent.quiz_stats.completed_quizzes}` : 
                  '0'}
              </div>
              <div className="stat-label">Total Quizzes</div>
            </div>
            
            <div className="stat-card">
              <h3>Quiz Accuracy</h3>
              <div className="stat-value">
                {selectedStudent.quiz_stats ? 
                  `${Math.round(selectedStudent.quiz_stats.accuracy_rate)}%` : 
                  'N/A'}
              </div>
              <div className="stat-label">Correct Answers</div>
            </div>
            
            <div className="stat-card">
              <h3>Total Score</h3>
              <div className="stat-value">
                {selectedStudent.quiz_stats ? 
                  selectedStudent.quiz_stats.total_score : 
                  '0'}
              </div>
              <div className="stat-label">Points Earned</div>
            </div>
            
            <div className="stat-card">
              <h3>Learning Buddy</h3>
              <div className="stat-value">
                {selectedStudent.buddy ? 
                  selectedStudent.buddy.charAt(0).toUpperCase() + selectedStudent.buddy.slice(1) : 
                  'None'}
              </div>
              <div className="stat-label">Selected</div>
            </div>
          </div>
          
          <div className="student-actions">
            <button className="action-button primary app-btn">Message Parent</button>
            <button className="action-button secondary app-btn">Schedule Meeting</button>
            <button className="action-button secondary app-btn" onClick={handleShowPerformanceGraph}>View Performance Graph</button>
          </div>
        </div>
      )}

      {/* Performance Graph Modal */}
      {showPerformanceGraph && selectedStudent && (
        <div className="performance-modal-overlay">
          <div className="performance-modal">
            <div className="modal-header">
              <h3>{selectedStudent.name} - Performance Graph</h3>
              <button className="close-button" onClick={handleClosePerformanceGraph}>×</button>
            </div>
            
            <div className="modal-content">
              <div className="performance-charts">
                <div className="chart-section">
                  <h4>Subject Performance Rankings</h4>
                  <div className="performance-chart">
                    {loadingSubjectData ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        Loading subject data...
                      </div>
                    ) : studentSubjectData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={studentSubjectData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                          <XAxis 
                            dataKey="subject" 
                            angle={0}
                            textAnchor="middle"
                            height={60}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis 
                            domain={[0, 100]}
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Score', angle: -90, position: 'insideLeft' }}
                          />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div style={{
                                    background: 'white',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    padding: '10px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                  }}>
                                    <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{data.subject}</p>
                                    <p style={{ margin: '0', fontSize: '12px' }}>Score: {data.score}</p>
                                    <p style={{ margin: '0', fontSize: '12px' }}>Accuracy: {data.accuracy}%</p>
                                    <p style={{ margin: '0', fontSize: '12px' }}>Total Quizzes: {data.total_quizzes}</p>
                                    <p style={{ margin: '0', fontSize: '12px' }}>Total Score: {data.total_score}</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                            {studentSubjectData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '40px', 
                        color: '#666',
                        fontSize: '14px'
                      }}>
                        No quiz data available yet. Student needs to take quizzes to see subject rankings.
                      </div>
                    )}
                  </div>
                </div>

                <div className="chart-section">
                  <h4>Emotional Progress</h4>
                  <div className="emotion-chart">
                    <div className="emotion-indicators">
                      <div className="emotion-item">
                        <span className="emotion-emoji">😊</span>
                        <span className="emotion-label">Happy</span>
                        <div className="emotion-bar">
                          <div className="emotion-fill" style={{ width: '90%' }}></div>
                        </div>
                        <span className="emotion-percentage">90%</span>
                      </div>
                      <div className="emotion-item">
                        <span className="emotion-emoji">😐</span>
                        <span className="emotion-label">Neutral</span>
                        <div className="emotion-bar">
                          <div className="emotion-fill" style={{ width: '8%' }}></div>
                        </div>
                        <span className="emotion-percentage">8%</span>
                      </div>
                      <div className="emotion-item">
                        <span className="emotion-emoji">😟</span>
                        <span className="emotion-label">Upset</span>
                        <div className="emotion-bar">
                          <div className="emotion-fill" style={{ width: '2%' }}></div>
                        </div>
                        <span className="emotion-percentage">2%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="chart-section">
                  <h4>Overall Progress Summary</h4>
                  <div className="progress-summary">
                    <div className="summary-card">
                      <h5>Academic Performance</h5>
                      <div className="summary-value excellent">Excellent</div>
                      <p>Consistent improvement in quiz scores</p>
                    </div>
                    <div className="summary-card">
                      <h5>Emotional Well-being</h5>
                      <div className="summary-value good">Very Good</div>
                      <p>Positive emotional state maintained</p>
                    </div>
                    <div className="summary-card">
                      <h5>Engagement Level</h5>
                      <div className="summary-value excellent">High</div>
                      <p>Active participation in sessions</p>
                    </div>
                  </div>
                </div>

                <div className="performance-actions">
                  <button className="action-button secondary app-btn">View Grade</button>
                  <button className="action-button primary app-btn" onClick={generateReport} style={{color: 'white'}}>Download Performance Report</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message to Parent Modal */}
      {showMessageModal && selectedStudent && (
        <div className="message-modal-overlay">
          <div className="message-modal">
            <div className="modal-header">
              <h3>Send Message to Parent</h3>
              <button className="close-button" onClick={handleCloseMessageModal}>×</button>
            </div>
            
            <div className="modal-content">
              <div className="message-info">
                <p><strong>Student:</strong> {selectedStudent.name}</p>
                <p><strong>Parent:</strong> {studentParents[selectedStudent.name]?.name}</p>
                <p><strong>Email:</strong> {studentParents[selectedStudent.name]?.email}</p>
              </div>

              <div className="form-group">
                <label>Message:</label>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Type your message to the parent here..."
                  className="message-textarea"
                  rows="4"
                />
              </div>

              <div className="modal-buttons">
                <button className="cancel-button app-btn" onClick={handleCloseMessageModal}>
                  Cancel
                </button>
                <button className="send-button app-btn" onClick={handleSendMessage}>
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderDashboardContent = () => (
    <div className="dashboard-content">
      <div className="content-header">
        <h1>Dashboard</h1>
        <div className="date-selector">
          <span>Last 30 days</span>
        </div>
      </div>
      
      <div className="dashboard-grid">
        <div className="dashboard-card large">
          <h3>Grade Distribution</h3>
          <div className="grade-chart">
            <div className="grade-bar">
              <span className="grade-label">A</span>
              <div className="grade-progress">
                <div className="grade-fill" style={{ width: '35%' }}></div>
              </div>
              <span className="grade-count">8 students</span>
            </div>
            <div className="grade-bar">
              <span className="grade-label">B</span>
              <div className="grade-progress">
                <div className="grade-fill" style={{ width: '45%' }}></div>
              </div>
              <span className="grade-count">11 students</span>
            </div>
            <div className="grade-bar">
              <span className="grade-label">C</span>
              <div className="grade-progress">
                <div className="grade-fill" style={{ width: '20%' }}></div>
              </div>
              <span className="grade-count">5 students</span>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <h3>Attendance Trend</h3>
          <div className="attendance-chart">
            <div className="chart-bar" style={{ height: '60%' }}></div>
            <div className="chart-bar" style={{ height: '80%' }}></div>
            <div className="chart-bar" style={{ height: '75%' }}></div>
            <div className="chart-bar" style={{ height: '90%' }}></div>
            <div className="chart-bar" style={{ height: '85%' }}></div>
          </div>
        </div>

        <div className="dashboard-card">
          <h3>Assignment Completion</h3>
          <div className="completion-stats">
            <div className="completion-item">
              <span className="completion-label">Completed</span>
              <span className="completion-value">18/24</span>
            </div>
            <div className="completion-item">
              <span className="completion-label">Pending</span>
              <span className="completion-value">6/24</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettingsContent = () => (
    <div className="settings-content">
      <div className="content-header">
        <h1>Settings</h1>
      </div>
      
      <div className="settings-grid">
        <div className="settings-card">
          <h3>Profile Settings</h3>
          <div className="setting-item">
            <label>Display Name</label>
            <input type="text" defaultValue="Ms. Johnson" />
          </div>
          <div className="setting-item">
            <label>Email</label>
            <input type="email" defaultValue="teacher@school.edu" />
          </div>
          <div className="setting-item">
            <label>Subject</label>
            <select defaultValue="Mathematics">
              <option>Mathematics</option>
              <option>Science</option>
              <option>English</option>
              <option>History</option>
            </select>
          </div>
        </div>

        <div className="settings-card">
          <h3>Notification Preferences</h3>
          <div className="setting-item">
            <label>
              <input type="checkbox" defaultChecked /> Email notifications
            </label>
          </div>
          <div className="setting-item">
            <label>
              <input type="checkbox" defaultChecked /> Assignment reminders
            </label>
          </div>
          <div className="setting-item">
            <label>
              <input type="checkbox" /> Parent meeting alerts
            </label>
          </div>
        </div>

        <div className="settings-card">
          <h3>Class Settings</h3>
          <div className="setting-item">
            <label>Default Grade Scale</label>
            <select defaultValue="A-F">
              <option>A-F</option>
              <option>0-100</option>
              <option>Pass/Fail</option>
            </select>
          </div>
          <div className="setting-item">
            <label>Attendance Tracking</label>
            <select defaultValue="Automatic">
              <option>Automatic</option>
              <option>Manual</option>
              <option>Disabled</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activePage) {
      case 'home':
        return renderHomeContent();
      case 'student-dashboard':
        return renderStudentDashboard();
      case 'dashboard':
        return renderDashboardContent();
      case 'settings':
        return renderSettingsContent();
      default:
        return renderHomeContent();
    }
  };

  return (
    <div className="teacher-dashboard-main-content">
      {renderContent()}
    </div>
  );
};

export default MainContent;